import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox, FormFieldInput } from '~/components/FormField';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { FormFieldTextArea } from '~/components/FormField/components/FormFieldTextArea';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { useCountriesList } from '~/modules/settings/api/queries/countryGroups';
import { Modal } from '~/modules/settings/components/Modal';
import { DISTRIBUTION_TERRITORIES } from '~settings/api/lib/types';

import type { DistributionGroup } from '../LabelForm/schema';
import { DistributionGroupSchema } from '../LabelForm/schema';

export interface DistributionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (data: DistributionGroup) => void;
  value?: DistributionGroup;
}

const DEFAULT_VALUES: DistributionGroup = {
  name: '',
  type: DISTRIBUTION_TERRITORIES.Worldwide,
  notes: '',
  distributionGroupCountries: [],
};

export function DistributionModal({
  isOpen,
  onOpenChange,
  onSubmit,
  value,
}: DistributionModalProps) {
  const { t } = useTranslation('settings');
  const distributionForm = useForm<DistributionGroup>({
    resolver: zodResolver(DistributionGroupSchema, {
      errorMap: getI18nErrorMap((s) => t(`label.validations.${s}`)),
    }),
    defaultValues: DEFAULT_VALUES,
    values: value,
  });

  useEffect(() => {
    if (!value) {
      distributionForm.reset(DEFAULT_VALUES);
    } else {
      distributionForm.reset(value);
    }
  }, [value, distributionForm]);

  const { countryList } = useCountriesList('EN', {
    select: (responseData) =>
      responseData.countryGroups
        .flatMap((group) =>
          group.countries.map((country) => ({
            value: country.id,
            label: `${country.name} (${country.code})`,
          })),
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    enabled: isOpen,
  });

  const canSelectCountries =
    distributionForm.watch('type') !== DISTRIBUTION_TERRITORIES.Worldwide;

  const submitAndClose = (data: DistributionGroup) => {
    onSubmit(data);
    distributionForm.reset();
  };

  return (
    <FormProvider {...distributionForm}>
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          distributionForm.reset();
          onOpenChange(open);
        }}
        title={
          value
            ? t('label.editDistributionGroup')
            : t('label.createDistributionGroup')
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                distributionForm.reset();
                onOpenChange(false);
              }}
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              onClick={distributionForm.handleSubmit(submitAndClose)}
            >
              {value ? t('label.updateGroup') : t('label.createGroup')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-lg">
          <FormFieldInput
            name="name"
            label={t('label.distributionGroupName')}
            placeholder={t('label.placeholder.distributionGroupName')}
            required
          />
          <Separator />
          <FormFieldRadioGroup
            name="type"
            label={t('label.territories')}
            variant="radio"
            options={[
              {
                value: DISTRIBUTION_TERRITORIES.Worldwide,
                label: t('label.everywhere'),
              },
              { value: DISTRIBUTION_TERRITORIES.OnlyIn, label: t('selected') },
              {
                value: DISTRIBUTION_TERRITORIES.WorldwideExclude,
                label: t('label.except'),
              },
            ]}
          />
          {canSelectCountries ? (
            <FormFieldCombobox
              name="distributionGroupCountries"
              label={t('countries')}
              options={countryList ?? []}
              multiselect
              displaySelectedOptions
              limitDisplayRows
              contentClassName="z-40"
            />
          ) : null}
          <Separator />
          <FormFieldTextArea
            name="notes"
            label={t('notes')}
            placeholder={t('label.placeholder.notes')}
            rows={4}
          />
        </div>
      </Modal>
    </FormProvider>
  );
}

DistributionModal.displayName = 'DistributionModal';
